/**
 * stripe-config.ts — plan types + display content for the subscription picker.
 *
 * Plans are NOT hardcoded here — the catalog is sourced from the Replay backend
 * `availablePlans` query (proxied via GET /api/stripe-plans). This module only
 * holds the UI copy (PLAN_CONTENT) and helpers to normalize/group plans.
 *
 * Ported from the dashboard's stripe-config.ts.
 */

/**
 * "Schedule a call" booking link for the 75%-off-first-month offer, surfaced on the out-of-credits
 * modal and the Subscription settings tab. Kept in sync with SCHEDULE_CALL_URL in the transactional
 * out-of-credits email (netlify/functions/lib/credits-exhausted-email.ts) so every touchpoint books
 * the same call.
 */
export const SCHEDULE_CALL_URL = 'https://calendly.com/tom-daly-replay/chat-with-tom-head-of-product'

/** Shape returned by the backend availablePlans query (via /api/stripe-plans) */
export interface BackendPlan {
  key: string
  name: string
  displayName: string
  tier: string
  interval: string
  visibility: string
  monthlyPriceCents: number
  billingId: string | null
}

export type PlanTier = 'free' | 'individual' | 'team' | 'growth' | 'enterprise' | 'private-cloud' | 'on-prem'
export type PlanInterval = 'month' | 'year' | 'none'

/** Normalised plan shape used by UI components */
export interface StripePlan {
  /** Stable key for referencing this plan in code (e.g., "free-v1") */
  key: string
  /** Name as the billing catalog knows it — kept raw so analytics payloads stay comparable */
  name: string
  /** Name shown to users (see planDisplayName) */
  displayName: string
  /** Stripe Price ID / billingId — null for the contact-sales tiers, which have no checkout */
  billingId: string | null
  /** Price per month in cents (0 for free, null when there is no published price) */
  monthlyPriceCents: number | null
  /** Billing interval */
  interval: PlanInterval
  /** Internal tier grouping */
  tier: PlanTier
  /**
   * Signing up requires a conversation first, so the card offers Contact Sales instead of checkout
   * and any price it shows is a starting point rather than what the customer will be billed.
   */
  contactSales: boolean
  /** Feature bullet list for plan card UI (currently sourced from PLAN_CONTENT) */
  features: string[]
}

/** Groups plans by tier for the monthly/yearly billing toggle UI */
export interface PlanTierGroup {
  tier: PlanTier
  /** Catalog name, as StripePlan.name */
  name: string
  /** Name shown to users, as StripePlan.displayName */
  displayName: string
  monthly: StripePlan
  yearly: StripePlan
  highlighted: boolean
  /** As StripePlan.contactSales — true for every plan in the group. */
  contactSales: boolean
}

/**
 * The contact-sales tiers, synthesized here rather than read from the catalog. Stripe has prices for
 * both, but they are deliberately absent from `availablePlans`: a customer has to talk to us before
 * signing up, so there is no self-serve checkout and no billingId to check out with. The price each
 * card shows is the published STARTING point — On-Prem in particular can rise with volume, which its
 * card says outright (see PLAN_CONTENT).
 *
 * If these ever go self-serve, they stop being synthetic: add them to the backend catalog with their
 * Stripe price ids and drop them from this list.
 */
const CONTACT_SALES_TIERS: { tier: PlanTier; name: string; fromMonthlyPriceCents: number }[] = [
  { tier: 'private-cloud', name: 'Private Cloud', fromMonthlyPriceCents: 100_000 },
  { tier: 'on-prem', name: 'On-Prem', fromMonthlyPriceCents: 500_000 },
]

/**
 * Tier → the plan name users see. Stripe, the Replay backend, and the database still call these
 * tiers `individual` and `team`, and the plan keys, checkout payloads, analytics properties, and
 * admin URLs all keep using them — this map is presentation only. `growth` is the legacy alias of
 * `team` (see groupPlansByTier), so it shows the same name.
 */
export const PLAN_DISPLAY_NAMES: Record<string, string> = {
  free: 'Free',
  individual: 'Starter',
  team: 'Pro',
  growth: 'Pro',
  enterprise: 'Enterprise',
  'private-cloud': 'Private Cloud',
  'on-prem': 'On-Prem',
}

/** Every tier, longest-lived first. Order is irrelevant — no tier name prefixes another. */
const PLAN_TIERS: PlanTier[] = [
  'free',
  'individual',
  'team',
  'growth',
  'enterprise',
  'private-cloud',
  'on-prem',
]

/**
 * The tier a plan key belongs to, e.g. "individual-v1-yearly" → "individual".
 *
 * Matched against the known tiers rather than split on the first "-", because two tier names contain
 * one: "private-cloud-v1" is Private Cloud, not a tier called "private". Anything unrecognized still
 * falls back to the leading segment so a brand-new tier degrades to something readable.
 */
export function tierFromPlanKey(key: string | null | undefined): string {
  const raw = (key ?? '').trim().toLowerCase()
  if (!raw) return ''
  const known = PLAN_TIERS.find((tier) => raw === tier || raw.startsWith(`${tier}-`))
  return known ?? raw.split('-')[0]!
}

/**
 * Tiers that include team collaboration — inviting teammates into a shared team that runs on one
 * plan's credits. Pro and up; Free and Starter see the feature and what it would cost them.
 *
 * Legacy `growth` is Pro under an older name (see PLAN_DISPLAY_NAMES), so it carries the same
 * entitlement. Enterprise, Private Cloud and On-Prem all sit above Pro.
 */
const TEAM_COLLABORATION_TIERS = new Set<string>([
  'team',
  'growth',
  'enterprise',
  'private-cloud',
  'on-prem',
])

/** The plan a team has to be on to invite people, named as users see it. */
export const TEAM_COLLABORATION_PLAN_NAME = PLAN_DISPLAY_NAMES['team']!

/**
 * Whether a plan may invite teammates. Shared by the client teaser and the server-side gate in
 * netlify/functions/lib/team-plan.ts so both agree on one definition.
 *
 * A missing key answers false: it means no active subscription, which is strictly less than Free.
 * Callers that cannot tell "no subscription" from "lookup failed" must not use this as the last word
 * — see teamCollaborationAccess, which fails open on a failed lookup so a billing outage never tells
 * a paying customer to upgrade.
 */
export function planIncludesTeamCollaboration(planKey: string | null | undefined): boolean {
  return TEAM_COLLABORATION_TIERS.has(tierFromPlanKey(planKey))
}

/**
 * The name to show for a plan. Resolves by tier when the caller knows it, otherwise by the catalog
 * name — which is the capitalized tier ("Individual", "Team"), so a caller holding nothing but a
 * plan name still lands on the right label. Anything unrecognized is returned unchanged.
 */
export function planDisplayName(name: string | null | undefined, tier?: string | null): string {
  const raw = (name ?? '').trim()
  return PLAN_DISPLAY_NAMES[(tier ?? raw).toLowerCase()] ?? raw
}

export const PLAN_CONTENT: Record<string, { tagline: string; description: string; note?: string }> = {
  free: {
    tagline: 'Try Replay with no commitment',
    description: '25 credits a month, no time limit, no credit card required.',
  },
  individual: {
    tagline: 'For individuals using Replay beyond the basics',
    description:
      '50 credits a month — the right volume for solo builders running a handful of apps or workflows.',
  },
  team: {
    tagline: 'For startups and small teams moving fast',
    description:
      '500 credits a month for teams that ship often and need consistent coverage — however they use Replay.',
  },
  growth: {
    tagline: 'For startups and small teams moving fast',
    description:
      '500 credits a month for teams that ship often and need consistent coverage — however they use Replay.',
  },
  enterprise: {
    tagline: 'For organizations using Replay at scale',
    description:
      'Custom analysis volume, contracts, and support — for however your organization uses Replay.',
  },
  'private-cloud': {
    tagline: 'For organizations that need to own their data',
    description:
      'A storage bucket your company owns, with no egress to other systems, and cluster resources dedicated to processing your data alone.',
  },
  'on-prem': {
    tagline: 'For running Replay inside your own infrastructure',
    description:
      'Complete ownership of the entire pipeline — data and compute — running on infrastructure you control.',
    note: 'Volume-based usage can increase this price.',
  },
}

/** Convert a backend plan response to the normalised StripePlan shape. */
export function normalizeBackendPlan(p: BackendPlan): StripePlan {
  const name = p.displayName || p.name
  return {
    key: p.key,
    name,
    displayName: planDisplayName(name, p.tier),
    billingId: p.billingId ?? null,
    monthlyPriceCents: p.monthlyPriceCents ?? null,
    interval: (p.interval as PlanInterval) || 'month',
    tier: (p.tier as PlanTier) || 'team',
    // A catalog plan carries a checkout price by definition, so anything the backend serves is
    // self-serve. The contact-sales tiers are synthesized in groupPlansByTier instead.
    contactSales: false,
    features: [],
  }
}

/**
 * Group a flat list of normalised plans by tier for the billing-toggle UI.
 * For each tier the monthly and yearly variants are paired; if only one interval
 * is available it is used for both slots. If both `team` and `growth` tiers are
 * present, only `team` is kept. The contact-sales tiers are always appended.
 */
export function groupPlansByTier(plans: StripePlan[]): PlanTierGroup[] {
  const byTier = new Map<PlanTier, { monthly?: StripePlan; yearly?: StripePlan }>()

  for (const plan of plans) {
    const entry = byTier.get(plan.tier) ?? {}
    if (plan.interval === 'year') {
      entry.yearly = plan
    } else {
      entry.monthly = plan
    }
    byTier.set(plan.tier, entry)
  }

  const tierOrder: PlanTier[] = ['free', 'individual', 'team', 'growth', 'enterprise']
  const groups: PlanTierGroup[] = []

  let hasTeam = false

  for (const tier of tierOrder) {
    const entry = byTier.get(tier)
    if (!entry) continue

    if (tier === 'team') hasTeam = true
    if (tier === 'growth' && hasTeam) continue

    const monthly = entry.monthly ?? entry.yearly!
    const yearly = entry.yearly ?? entry.monthly!
    const highlighted = tier === 'team' || tier === 'growth'

    groups.push({
      tier,
      name: monthly.name,
      displayName: monthly.displayName,
      monthly,
      yearly,
      highlighted,
      contactSales: monthly.contactSales,
    })
  }

  // The contact-sales tiers close out the row, to the right of the self-serve plans. They have no
  // catalog row so they never appear in `plans` — synthesize each one here, skipping any the backend
  // did supply (which would mean it went self-serve and owns its own card).
  for (const { tier, name, fromMonthlyPriceCents } of CONTACT_SALES_TIERS) {
    if (groups.some((group) => group.tier === tier)) continue
    const plan: StripePlan = {
      key: tier,
      name,
      displayName: planDisplayName(name, tier),
      billingId: null,
      monthlyPriceCents: fromMonthlyPriceCents,
      interval: 'month',
      tier,
      contactSales: true,
      features: [],
    }
    groups.push({
      tier,
      name: plan.name,
      displayName: plan.displayName,
      monthly: plan,
      yearly: plan,
      highlighted: false,
      contactSales: true,
    })
  }

  return groups
}
